import {
  formatDateInTimezone,
  getCurrentUserTz,
} from 'module/helpers/timezone';
import { useTranslate } from 'module/hooks/use-translation';
import { Loading } from 'module/ui/components/atoms/loading';
import { toHHMMSS } from 'module/ui/core/time-covertor';
import { faEnvelope, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@rocketlane/ui';
import { useQuery } from '@tanstack/react-query';
import { CitationAPI } from 'api/citation';
import { CitationType } from 'api/citation/types';
import { useCurrentUserQuery } from 'queries/account';
import { CitationObject, CitationResponse } from 'queries/citation';
import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  Label,
  CitationDataPopoverContainer,
  StyledLoader,
  SummaryContainer,
  SummaryLabel,
  SummaryText,
  CitationDataWrapper,
} from './shared-styles';
import { OnViewCitationSourceCallback } from '../types';

interface CitationDataPopoverProps {
  citationId: number;
  onViewCitationSource?: OnViewCitationSourceCallback;
}

const CitationTimeStampWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  font-size: var(--font-size-small);
  color: var(--text-secondary);
`;

const CitationTimeStamp = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

const EmailCitationTimestampRow = styled.div<{ $hasTimestamp: boolean }>`
  display: flex;
  align-items: center;
  /* With no timestamp, the View button is the only child — space-between
     would leave it flush left since there's nothing to push it away from. */
  justify-content: ${({ $hasTimestamp }) =>
    $hasTimestamp ? 'space-between' : 'flex-end'};
  gap: var(--spacing-03);
  width: 100%;
`;

const CitationDataContainer = styled.div<{
  $hasGreyBackground?: boolean;
  $isEmail?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
  padding: ${({ $isEmail }) =>
    $isEmail ? 'var(--spacing-04)' : '0 var(--spacing-05)'};
  background-color: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? 'var(--scarlet-gray-30)' : 'transparent'};
  border-radius: ${({ $hasGreyBackground }) =>
    $hasGreyBackground ? '8px' : '0'};
`;

const StyledCitationContent = styled.p`
  font-style: italic;
`;

export const CitationMeetingPagePopover: React.FC<CitationDataPopoverProps> = ({
  citationId,
  onViewCitationSource,
}) => {
  const t = useTranslate();
  const { data: currentUser } = useCurrentUserQuery();
  const userTimeZone = currentUser?.user.timeZone || getCurrentUserTz();
  const [citationData, setCitationData] = useState<CitationObject[]>([]);
  const { data, isFetching, isSuccess, isError } = useQuery<
    CitationResponse,
    Error
  >({
    queryKey: ['rl-citation', citationId],
    queryFn: () => CitationAPI.getCitation(citationId),
  });

  useEffect(() => {
    if (isSuccess || isError) {
      setCitationData(data?.metaData?.transcriptTextBlocks ?? []);
    }
  }, [data, isSuccess, isError]);

  const hasSummary = !!data?.metaData?.summary;
  const isEmail = data?.citationType === 'EMAIL';
  const summaryLabel = isEmail
    ? t('citation.email_summary')
    : t('citation.transcript_summary');

  return (
    <>
      {isFetching ? (
        <StyledLoader>
          <Loading />
        </StyledLoader>
      ) : (
        <CitationDataPopoverContainer>
          {data?.metaData?.source?.meetingName && (
            <Label>{data?.metaData?.source?.meetingName}</Label>
          )}

          {hasSummary && (
            <SummaryContainer
              $hasMarginTop={!data?.metaData?.source?.meetingName}
            >
              <SummaryLabel>{summaryLabel}</SummaryLabel>
              <SummaryText>{data?.metaData?.summary}</SummaryText>
            </SummaryContainer>
          )}

          <CitationDataWrapper $hasGreyBackground={hasSummary}>
            {citationData?.map((citation, index) => (
              <CitationDataContainer
                key={index}
                className="relative"
                $isEmail={isEmail}
              >
                {citation.fromTime && citation.toTime ? (
                  <>
                    <CitationTimeStampWrapper>
                      <FontAwesomeIcon icon={faVideo} size="sm" />
                      <CitationTimeStamp>
                        {toHHMMSS(parseInt(citation.fromTime ?? '0'))} -{' '}
                        {toHHMMSS(parseInt(citation.toTime ?? '0'))}
                      </CitationTimeStamp>
                    </CitationTimeStampWrapper>
                    {onViewCitationSource && (
                      <Button
                        variant="ghost"
                        size="small"
                        className="absolute right-[var(--spacing-05)] top-0"
                        onClick={() => {
                          if (!data) {
                            return;
                          }
                          if (data.citationType === CitationType.Meeting) {
                            onViewCitationSource?.({
                              citationType: CitationType.Meeting,
                              citation: data,
                              currentBlock: citation,
                            });
                          }
                        }}
                      >
                        <FormattedMessage id="view" />
                      </Button>
                    )}
                  </>
                ) : (
                  // Email citations don't have per-block timestamps — the
                  // whole citation points at one thread, so the timestamp +
                  // "View" action only makes sense once, on the first block.
                  // The timestamp itself is optional display info (missing
                  // source.updatedAt just hides it) — it must not block the
                  // View action, which only needs `data` to navigate. Skip
                  // the row entirely when neither would render anything.
                  isEmail &&
                  index === 0 &&
                  (() => {
                    const hasTimestamp = !!data?.metaData?.source?.updatedAt;
                    const hasViewButton = !!onViewCitationSource && !!data;

                    if (!hasTimestamp && !hasViewButton) {
                      return null;
                    }

                    return (
                      <EmailCitationTimestampRow $hasTimestamp={hasTimestamp}>
                        {hasTimestamp && (
                          <CitationTimeStampWrapper>
                            <FontAwesomeIcon icon={faEnvelope} size="sm" />
                            <CitationTimeStamp>
                              {formatDateInTimezone(
                                data!.metaData.source.updatedAt,
                                userTimeZone,
                                'ddd DD MMM, h:mm A',
                              )}
                            </CitationTimeStamp>
                          </CitationTimeStampWrapper>
                        )}
                        {hasViewButton && (
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => {
                              onViewCitationSource?.({
                                citationType: CitationType.Email,
                                citation: data!,
                              });
                            }}
                          >
                            <FormattedMessage id="view" />
                          </Button>
                        )}
                      </EmailCitationTimestampRow>
                    );
                  })()
                )}

                <strong>{citation.speaker}</strong>
                <StyledCitationContent>{`"${citation.content}"`}</StyledCitationContent>
              </CitationDataContainer>
            ))}
          </CitationDataWrapper>
        </CitationDataPopoverContainer>
      )}
    </>
  );
};
